// Axios instance con interceptor auth, refresh token e gestione errori globali
import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { queryClient } from './queryClient'

const BASE_URL = import.meta.env.VITE_API_URL || ''

export const api = axios.create({
  baseURL: `${BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
})

// ===== INTERCEPTOR REQUEST: aggiunge Authorization header =====
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Per FormData lascia che il browser imposti Content-Type con il boundary corretto
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    // Legge il token direttamente da localStorage (evita dipendenza circolare con lo store)
    const stored = localStorage.getItem('fc-auth')
    if (stored) {
      try {
        const { state } = JSON.parse(stored) as { state: { accessToken: string | null } }
        if (state.accessToken) {
          config.headers.Authorization = `Bearer ${state.accessToken}`
        }
      } catch {
        // Ignora errori di parsing
      }
    }
    return config
  },
  (error: unknown) => Promise.reject(error)
)

// Flag per evitare loop di refresh
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value: string) => void
  reject: (error: unknown) => void
}> = []

function processQueue(error: unknown, token: string | null): void {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else if (token) {
      resolve(token)
    }
  })
  failedQueue = []
}

// ===== INTERCEPTOR RESPONSE: gestisce 401 con refresh =====
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return api(originalRequest)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const stored = localStorage.getItem('fc-auth')
        if (!stored) throw new Error('No stored auth')
        const { state } = JSON.parse(stored) as { state: { refreshToken: string | null } }
        if (!state.refreshToken) throw new Error('No refresh token')

        const response = await axios.post<{ accessToken: string; refreshToken: string }>(
          `${BASE_URL || ''}/api/auth/refresh`,
          { refreshToken: state.refreshToken }
        )
        const { accessToken, refreshToken } = response.data

        // Aggiorna lo store Zustand tramite localStorage
        const parsed = JSON.parse(stored) as { state: Record<string, unknown> }
        parsed.state.accessToken = accessToken
        parsed.state.refreshToken = refreshToken
        localStorage.setItem('fc-auth', JSON.stringify(parsed))

        processQueue(null, accessToken)
        originalRequest.headers.Authorization = `Bearer ${accessToken}`
        return api(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        // Forza logout
        localStorage.removeItem('fc-auth')
        queryClient.clear()
        window.location.href = '/login'
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default api
